import type {
  AnnouncementButton,
  RenderedAnnouncementContent,
} from "@/server-types";
import { Button } from "@heroui/react";
import { useEffect, useRef } from "react";
import { isDashboardPath } from "./announcementActions";
import { ANNOUNCEMENT_HTML_CLASSES } from "./announcementHtmlClasses";
import { sanitizeAnnouncementHtml } from "./sanitizeAnnouncementHtml";

type AnnouncementContentProps = {
  content: RenderedAnnouncementContent;
  onButtonPress: (button: AnnouncementButton) => void;
  onDashboardLink: (path: string) => void;
};

export function AnnouncementContent({
  content,
  onButtonPress,
  onDashboardLink,
}: AnnouncementContentProps) {
  const bodyRef = useRef<HTMLDivElement>(null);

  // A native listener: the links live in injected HTML, so a React onClick would need a div posing as a control.
  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;

    function openDashboardLink(event: globalThis.MouseEvent) {
      if (!(event.target instanceof Element)) return;
      const href = event.target.closest("a")?.getAttribute("href");
      if (href && isDashboardPath(href)) {
        event.preventDefault();
        onDashboardLink(href);
      }
    }

    body.addEventListener("click", openDashboardLink);
    return () => body.removeEventListener("click", openDashboardLink);
  }, [onDashboardLink]);

  return (
    <div className="flex flex-col gap-6">
      <div
        ref={bodyRef}
        className={ANNOUNCEMENT_HTML_CLASSES}
        dangerouslySetInnerHTML={{
          __html: sanitizeAnnouncementHtml(content.html),
        }}
      />
      {content.buttons.length > 0 && (
        <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          {content.buttons.map((button) => (
            <Button
              key={button.id}
              color={button.style === "primary" ? "primary" : "default"}
              variant={button.style === "primary" ? "solid" : "light"}
              onPress={() => onButtonPress(button)}
              data-identifier={`announcement-button-${button.id}`}
            >
              {button.label}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}
